import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FloppyDisk, Plus, TrashSimple, X } from "phosphor-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fpatch, fpost } from "../../../API/callsAPI";
import useStyles from "./CommonFilterComponent.styles";
import { hasValidRows, isDirtyTuples, rowsToTuples } from "./filterRowUtils";
import { fetchFilterOptionList } from "./filterOptionLoader";

const menuProps = {
  PaperProps: {
    sx: { bgcolor: "#252A38", color: "#F9F9F9", maxHeight: 350 },
  },
};
//v
const FETCHABLE_AUTOCOMPLETE_FIELDS = [
  "allowed_dept",
  "status",
  "task",
  "type",
];
const OBJECT_AUTOCOMPLETE_FIELDS = ["allowed_dept", "status", "task"];
//v
const isCustomDropdownField = (field) =>
  String(field?.type || "").toLowerCase() === "dropdown";
//v
const getCustomDropdownOptions = (field) => {
  const optionMap = field?.options?.options;
  if (!optionMap || typeof optionMap !== "object") return [];
  return Object.values(optionMap)
    .map((value) => String(value).trim())
    .filter(Boolean);
};
//v
const getValueInputType = (field) => {
  if (!field) return "text";
  if (isCustomDropdownField(field)) return "autocomplete";
  if (["created_at", "updated_at"].includes(field.name)) return "date";
  if (FETCHABLE_AUTOCOMPLETE_FIELDS.includes(field.name)) return "autocomplete";
  return "text";
};

const listboxSx = {
  sx: { backgroundColor: "#252A38", color: "#F9F9F9" },
};

const FilterValueAutocomplete = ({
  field,
  row,
  optionCache,
  fetchOptionsForField,
  updateRow,
  disabled,
  sx,
}) => {
  const fieldKey = field?.name;
  const isDropdown = isCustomDropdownField(field);

  const renderInput = (params) => (
    <TextField {...params} size="small" placeholder="Select value" />
  );

  // Custom field dropdown: options on field, string label + string value
  if (isDropdown) {
    const options = getCustomDropdownOptions(field);
    return (
      <Autocomplete
        options={options}
        value={row.value || ""}
        disabled={disabled}
        onChange={(_, newValue) => updateRow(row.id, { value: newValue || "" })}
        sx={sx}
        ListboxProps={listboxSx}
        renderInput={renderInput}
      />
    );
  }

  // Asset type: fetched strings, string label + string value
  if (fieldKey === "type") {
    if (optionCache.type == null) {
      return (
        <TextField size="small" disabled placeholder="Loading..." sx={sx} />
      );
    }
    const options = optionCache.type.filter(
      (option) => typeof option === "string" && option.trim(),
    );
    return (
      <Autocomplete
        options={options}
        value={row.value || ""}
        disabled={disabled}
        onChange={(_, newValue) => updateRow(row.id, { value: newValue || "" })}
        sx={sx}
        ListboxProps={listboxSx}
        renderInput={renderInput}
      />
    );
  }

  // Dept / status / task: fetched objects, name label + id value
  if (OBJECT_AUTOCOMPLETE_FIELDS.includes(fieldKey)) {
    if (optionCache[fieldKey] == null) {
      return (
        <TextField size="small" disabled placeholder="Loading..." sx={sx} />
      );
    }
    const options = optionCache[fieldKey];
    const selected =
      options.find((option) => Number(option.id) === Number(row.value)) ?? null;

    return (
      <Autocomplete
        options={options}
        value={selected}
        disabled={disabled}
        getOptionLabel={(option) => option.name || option.title || ""}
        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
        onChange={(_, newValue) =>
          updateRow(row.id, {
            value: newValue?.id != null ? String(newValue.id) : "",
          })
        }
        sx={sx}
        ListboxProps={listboxSx}
        renderInput={renderInput}
      />
    );
  }

  return null;
};

const FilterSettings = ({
  loggedInUser,
  handleDeleteFilter,
  optionCache,
  setOptionCache,
  rows,
  setRows,
  defaultFields,
  trackableId,
  projectId,
  handleUpdateRefresher,
  currentSelectedFilter,
  baselineTuples = [],
  appliedFilterForTrackable = null,
  onApply,
  onClear,
  onSaveSuccess,
}) => {
  const classes = useStyles();
  const [saveFilterAnchorEl, setSaveFilterAnchorEl] = useState(null);
  const [saveFilterDraftName, setSaveFilterDraftName] = useState("");

  const optionsFetchInFlightRef = useRef(new Set());

  const fetchOptionsForField = useCallback(
    async (fieldKey) => {
      if (optionsFetchInFlightRef.current.has(fieldKey)) return;

      optionsFetchInFlightRef.current.add(fieldKey);
      try {
        const data = await fetchFilterOptionList(fieldKey, projectId);
        setOptionCache((prev) => ({ ...prev, [fieldKey]: data }));
      } catch (error) {
        console.error(`Error loading filter options (${fieldKey})`, error);
        setOptionCache((prev) => ({ ...prev, [fieldKey]: [] }));
      } finally {
        optionsFetchInFlightRef.current.delete(fieldKey);
      }
    },
    [projectId, setOptionCache],
  );

  useEffect(() => {
    if (
      FETCHABLE_AUTOCOMPLETE_FIELDS.every((key) => optionCache[key] == null)
    ) {
      optionsFetchInFlightRef.current.clear();
    }
  }, [optionCache]);

  useEffect(() => {
    rows.forEach((row) => {
      const field = defaultFields.find((f) => f.name === row.filterType);
      if (!field || isCustomDropdownField(field)) return;
      if (!FETCHABLE_AUTOCOMPLETE_FIELDS.includes(row.filterType)) return;
      if (optionCache[row.filterType] != null) return;
      fetchOptionsForField(row.filterType);
    });
  }, [rows, defaultFields, fetchOptionsForField, optionCache]);

  const currentTuples = useMemo(() => rowsToTuples(rows), [rows]);
  const isDirty = useMemo(
    () => isDirtyTuples(baselineTuples, currentTuples),
    [baselineTuples, currentTuples],
  );
  const rowsAreValid = useMemo(() => hasValidRows(rows), [rows]);
  const isCurrentFilterAppliedHere = useMemo(
    () =>
      appliedFilterForTrackable?.id != null &&
      currentSelectedFilter?.id != null &&
      Number(appliedFilterForTrackable.id) === Number(currentSelectedFilter.id),
    [appliedFilterForTrackable, currentSelectedFilter],
  );

  const canSave = rowsAreValid && isDirty;
  const canApply = rowsAreValid && (isDirty || !isCurrentFilterAppliedHere);
  const canClear =
    rows.length > 0 ||
    !!currentSelectedFilter?.id ||
    !!appliedFilterForTrackable?.id;

  const createEmptyRow = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    filterType: "",
    operator: "",
    value: "",
  });

  const updateRow = (id, updates) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  };

  const handleAddRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const handleRemoveRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleOpenSaveFilterPopover = (event) => {
    setSaveFilterDraftName(currentSelectedFilter?.name ?? "");
    setSaveFilterAnchorEl(event.currentTarget);
  };

  const handleSaveFilterClick = (event) => {
    if (currentSelectedFilter?.id) {
      handleSaveFilter();
      return;
    }
    handleOpenSaveFilterPopover(event);
  };

  const handleCloseSaveFilterPopover = () => {
    setSaveFilterAnchorEl(null);
  };

  const handleSaveFilter = async () => {
    if (!rowsAreValid) return;

    const filters = rowsToTuples(rows);

    try {
      if (currentSelectedFilter?.id) {
        const name = saveFilterDraftName.trim() || currentSelectedFilter.name;
        const res = await fpatch({
          url: `/trackables/filter/${currentSelectedFilter.id}/`,
          data: {
            trackable: trackableId,
            project: projectId,
            name,
            filters,
          },
        });
        if (res.status === 200 || res.status === 201) {
          onSaveSuccess?.();
        }
      } else {
        const name = saveFilterDraftName.trim();
        if (!name) return;

        const res = await fpost({
          url: "/trackables/filter/",
          data: {
            trackable: trackableId,
            project: projectId,
            name,
            filters,
          },
        });
        if (res.status === 200 || res.status === 201) {
          onSaveSuccess?.();
        }
      }
    } catch (error) {
      console.error("Error saving filter", error);
    }
    handleCloseSaveFilterPopover();
    setSaveFilterDraftName("");
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Typography sx={{ color: "#B1B5C9" }}>Filter Applied</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Chip
              label={appliedFilterForTrackable?.name ?? "None"}
              sx={{
                bgcolor: "#6C65D929",
                color: "#ABA6FF",
              }}
            />
            <IconButton
              size="small"
              aria-label="Remove filter"
              sx={{
                background: "#6C65D929",
                color: "#ABA6FF",
                "&:hover": { bgcolor: "#6C65D929" },
              }}
              disabled={!canClear}
              onClick={onClear}
            >
              <X size={18} />
            </IconButton>
            {String(appliedFilterForTrackable?.created_by?.id) ===
              String(loggedInUser?.id) && (
              <IconButton
                size="small"
                aria-label="Remove filter"
                sx={{
                  background: "#E0383829",
                  color: "#D61F1F",
                  "&:hover": { bgcolor: "#E0383829" },
                }}
                onClick={() => handleDeleteFilter(appliedFilterForTrackable)}
              >
                <TrashSimple size={18} />
              </IconButton>
            )}
          </Box>
        </Stack>
        <Button
          disabled={!canSave}
          onClick={handleSaveFilterClick}
          className={classes.saveBtn}
          startIcon={<FloppyDisk size={18} />}
        >
          Save Filter
        </Button>
        <Popover
          id="save-filter-popover"
          open={Boolean(saveFilterAnchorEl)}
          anchorEl={saveFilterAnchorEl}
          onClose={handleCloseSaveFilterPopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                bgcolor: "#1B1D28",
                color: "#F9F9F9",
                border: "1px solid #606479",
                borderRadius: "8px",
                p: 2,
                minWidth: 280,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
              },
            },
          }}
        >
          <Stack spacing={2}>
            <TextField
              size="small"
              fullWidth
              placeholder="(Optional) Filter name"
              value={saveFilterDraftName}
              onChange={(e) => setSaveFilterDraftName(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#252A38",
                  color: "#F9F9F9",
                  borderRadius: "5px",
                  "& fieldset": { borderColor: "#606479" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
                  "&.Mui-focused fieldset": { borderColor: "#6C65D9" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#606479",
                  opacity: 1,
                },
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCloseSaveFilterPopover}
                className={classes.projectOutlinedButtonSx}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={handleSaveFilter}
                disabled={
                  currentSelectedFilter?.id
                    ? !rowsAreValid
                    : !saveFilterDraftName.trim() || !rowsAreValid
                }
                className={classes.projectFilledButtonSx}
                sx={{
                  "&.Mui-disabled": {
                    bgcolor: "rgba(108, 101, 217, 0.35) !important",
                    color: "rgba(238, 238, 238, 0.45) !important",
                  },
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Popover>
      </Box>

      <>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 1, pr: 5, display: { xs: "none", sm: "flex" } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.labelStyle}>Filter Type</Typography>
          </Box>
          <Box sx={{ width: 140, flexShrink: 0 }}>
            <Typography className={classes.labelStyle}>Operator</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.labelStyle}>Value</Typography>
          </Box>
          <Box sx={{ width: 40 }} />
        </Stack>

        <Stack spacing={1.5}>
          {rows.map((row) => {
            const field = defaultFields.find((f) => f.name === row.filterType);
            const operators =
              field?.allowed_operations || field?.allowed_operators || [];
            return (
              <Stack
                key={row.id}
                direction="row"
                spacing={1}
                alignItems="flex-end"
              >
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    displayEmpty
                    value={row.filterType}
                    onChange={(e) => {
                      const selectedField = defaultFields.find(
                        (f) => f.name === e.target.value,
                      );
                      const nextOperator =
                        selectedField?.allowed_operations?.[0] ||
                        selectedField?.allowed_operators?.[0] ||
                        "";
                      if (
                        FETCHABLE_AUTOCOMPLETE_FIELDS.includes(
                          e.target.value,
                        ) &&
                        optionCache[e.target.value] == null
                      ) {
                        fetchOptionsForField(e.target.value);
                      }
                      updateRow(row.id, {
                        filterType: e.target.value,
                        operator: nextOperator,
                        value: "",
                      });
                    }}
                    MenuProps={menuProps}
                    className={classes.selectStyle}
                  >
                    {defaultFields.map((f) => (
                      <MenuItem
                        key={f.name}
                        value={f.name}
                        sx={{ color: "#F9F9F9" }}
                      >
                        {f.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  sx={{ width: { xs: 100, sm: 140 }, flexShrink: 0 }}
                >
                  <Select
                    displayEmpty
                    value={row.operator}
                    disabled={!row.filterType || operators.length === 0}
                    onChange={(e) =>
                      updateRow(row.id, { operator: e.target.value })
                    }
                    MenuProps={menuProps}
                    className={classes.selectStyle}
                  >
                    {operators.map((op) => (
                      <MenuItem key={op} value={op} sx={{ color: "#F9F9F9" }}>
                        {op}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(() => {
                  const inputType = getValueInputType(field);
                  const commonInputSx = {
                    flex: 1,
                    minWidth: 0,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#252A38",
                      color: "#F9F9F9",
                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.25)",
                      },
                      "&.Mui-focused fieldset": { borderColor: "#606479" },
                    },
                  };

                  if (inputType === "date") {
                    return (
                      <TextField
                        type="date"
                        size="small"
                        value={row.value || ""}
                        disabled={!row.operator}
                        onChange={(e) =>
                          updateRow(row.id, { value: e.target.value })
                        }
                        sx={commonInputSx}
                      />
                    );
                  }

                  if (inputType === "autocomplete") {
                    return (
                      <FilterValueAutocomplete
                        field={field}
                        row={row}
                        optionCache={optionCache}
                        fetchOptionsForField={fetchOptionsForField}
                        updateRow={updateRow}
                        disabled={!row.operator}
                        sx={commonInputSx}
                      />
                    );
                  }

                  return (
                    <TextField
                      size="small"
                      placeholder="Enter value"
                      value={row.value}
                      disabled={!row.operator}
                      onChange={(e) =>
                        updateRow(row.id, { value: e.target.value })
                      }
                      sx={{
                        ...commonInputSx,
                        "& .MuiInputBase-input::placeholder": {
                          color: "#f9f9f9 !important",
                          opacity: 1,
                        },
                      }}
                    />
                  );
                })()}
                <IconButton
                  size="small"
                  aria-label="Remove filter row"
                  onClick={() => handleRemoveRow(row.id)}
                  sx={{
                    color: "#D61F1F",
                    mb: 0.25,
                    "&:hover": { bgcolor: "rgba(239, 68, 68, 0.12)" },
                  }}
                >
                  <TrashSimple size={18} />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleAddRow}
            sx={{
              color: "#F9F9F9",
              textTransform: "none",
              fontWeight: 500,
              "& .MuiButton-startIcon": { mr: 0.75 },
              "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
            }}
          >
            Add a filter
          </Button>
        </Box>
      </>

      <Stack
        direction="row"
        spacing={1}
        style={{ position: "absolute", bottom: 0, width: "100%" }}
      >
        <Button
          fullWidth
          className={classes.projectOutlinedButtonSx}
          disabled={!canClear}
          onClick={onClear}
          sx={{
            "&.Mui-disabled": {
              bgcolor: "transparent",
              color: "rgba(238, 238, 238, 0.45) !important",
            },
          }}
        >
          Clear
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={!canApply}
          onClick={onApply}
          className={classes.projectFilledButtonSx}
          sx={{
            "&.Mui-disabled": {
              bgcolor: "rgba(108, 101, 217, 0.35) !important",
              color: "rgba(238, 238, 238, 0.45) !important",
            },
          }}
        >
          Apply Filter
        </Button>
      </Stack>
    </div>
  );
};

export default FilterSettings;
